import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// 拖动距离阈值：移动超过此像素才算拖动，否则视为点击
const DRAG_THRESHOLD = 5;

// 可拖动 Hook
const useDraggable = (id, defaultPosition = { x: 0, y: 0 }) => {
  const [position, setPosition] = useState(() => {
    // 从 localStorage 恢复位置
    try {
      const saved = localStorage.getItem(`draggable-${id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (e) {
      console.log('恢复位置失败:', e);
    }
    return defaultPosition;
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const handleStart = (e) => {
    // 如果触摸的是按钮、链接等交互元素，不启动拖动，让点击事件正常触发
    let target = e.target;
    
    // 如果目标不是元素节点（可能是文本节点），获取其父元素
    if (target.nodeType !== 1) {
      target = target.parentElement;
    }
    
    // 确保我们有有效的元素
    if (!target || !target.tagName) {
      return;
    }
    
    const interactiveTag = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName);
    const interactiveRole = target.closest && target.closest('button, a, input, select, textarea, [role="button"]');
    if (interactiveTag || interactiveRole) {
      return;
    }

    // 记录起始位置用于判断是拖动还是点击
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startPos.current = { x: clientX, y: clientY };
    hasMoved.current = false;
    
    // 计算拖动偏移量
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
    
    setIsDragging(true);
  };

  const handleMove = (e) => {
    if (!isDragging) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // 判断是否超过拖动阈值
    const dx = clientX - startPos.current.x;
    const dy = clientY - startPos.current.y;
    if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) {
      return; // 未超过阈值，不开始拖动
    }
    
    hasMoved.current = true;
    
    // 阻止触摸移动时的默认滚动行为
    if (e.cancelable) {
      e.preventDefault();
    }

    const newX = clientX - dragOffset.current.x;
    const newY = clientY - dragOffset.current.y;
    
    setPosition({ x: newX, y: newY });
  };

  const handleEnd = (e) => {
    if (isDragging) {
      setIsDragging(false);
      
      // 只有真正拖动了才保存位置
      if (hasMoved.current) {
        // 如果是拖动操作，阻止按钮点击事件
        if (e && e.cancelable) {
          e.preventDefault();
        }
        try {
          localStorage.setItem(`draggable-${id}`, JSON.stringify(position));
        } catch (err) {
          console.log('保存位置失败:', err);
        }
      }
      hasMoved.current = false;
    }
  };

  useEffect(() => {
    if (isDragging) {
      // 在拖动时，防止页面滚动
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.userSelect = 'none';
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        // 拖动结束时，恢复页面滚动
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        document.body.style.userSelect = '';
        
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging]);

  const resetPosition = () => {
    setPosition(defaultPosition);
    try {
      localStorage.removeItem(`draggable-${id}`);
    } catch (e) {
      console.log('重置位置失败:', e);
    }
  };

  return { position, isDragging, handleStart, resetPosition: resetPosition };
};

// 响应式屏幕宽度hook
const useScreenWidth = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return screenWidth;
};

// 可拖动包装器组件
const DraggableWrapper = ({ children, dragState, className = '', originalClassName = '', enabled = true }) => {
  // 如果没有被拖动过（位置都是 0），使用原始定位
  const hasDragged = dragState.position.x !== 0 || dragState.position.y !== 0;
  
  if (!enabled) {
    // 使用原始定位，不启用拖动
    return (
      <div className={className}>
        {children}
      </div>
    );
  }
  
  // 拖动时的样式
  const dragStyle = {
    cursor: 'grab',
    userSelect: 'none',
    // 只在拖动中才设置 touchAction: none，否则保持默认让按钮可点击
    ...(dragState.isDragging ? { touchAction: 'none', pointerEvents: 'none' } : {}),
  };
  
  if (!hasDragged) {
    // 使用原始定位，但添加触摸事件处理器和样式
    return (
      <div
        className={className}
        style={dragStyle}
        onMouseDown={dragState.handleStart}
        onTouchStart={dragState.handleStart}
      >
        {children}
      </div>
    );
  }
  
  // 使用拖动后的固定定位
  return (
    <div
      className={`fixed ${originalClassName}`}
      style={{
        ...dragStyle,
        left: dragState.position.x,
        top: dragState.position.y,
        zIndex: 9999,
      }}
      onMouseDown={dragState.handleStart}
      onTouchStart={dragState.handleStart}
    >
      {children}
    </div>
  );
};

// 落子声音
const playStoneSound = () => {
  try {
    const audio = new Audio('/stone-sound.wav');
    audio.play();
  } catch (error) {
    console.log('播放落子声音失败:', error);
  }
};

// 提子声音
const playCaptureSound = () => {
  try {
    const audio = new Audio('/capture-sound.wav');
    audio.play();
  } catch (error) {
    console.log('播放提子声音失败:', error);
  }
};

const PRESET_BACKGROUNDS = [
  { id: 'bamboo', name: '竹色', color: '#E8D4A8', border: '#8B7355' },
  { id: 'cherry', name: '樱桃木', color: '#B87333', border: '#6B3A1F' },
  { id: 'oak', name: '橡木色', color: '#C4A882', border: '#8B5A2B' },
  { id: 'walnut', name: '胡桃色', color: '#D4B896', border: '#6B4423' },
  { id: 'teak', name: '柚木色', color: '#CD853F', border: '#8B5A2B' },
  { id: 'dark', name: '深色', color: '#8B7355', border: '#5D4037' },
];

const GoUtils = {
  getNeighbors(row, col, boardSize) {
    const neighbors = [];
    if (row > 0) neighbors.push([row - 1, col]);
    if (row < boardSize - 1) neighbors.push([row + 1, col]);
    if (col > 0) neighbors.push([row, col - 1]);
    if (col < boardSize - 1) neighbors.push([row, col + 1]);
    return neighbors;
  },
  getGroup(row, col, board, boardSize) {
    const color = board[row][col];
    if (!color) return [];
    const group = [];
    const visited = new Set();
    const stack = [[row, col]];
    while (stack.length > 0) {
      const [r, c] = stack.pop();
      const key = `${r},${c}`;
      if (visited.has(key)) continue;
      if (r < 0 || r >= boardSize || c < 0 || c >= boardSize) continue;
      if (board[r][c] !== color) continue;
      visited.add(key);
      group.push([r, c]);
      const neighbors = this.getNeighbors(r, c, boardSize);
      for (const [nr, nc] of neighbors) {
        if (!visited.has(`${nr},${nc}`) && board[nr][nc] === color) {
          stack.push([nr, nc]);
        }
      }
    }
    return group;
  },
  getLiberties(group, board, boardSize) {
    const liberties = new Set();
    for (const [row, col] of group) {
      const neighbors = this.getNeighbors(row, col, boardSize);
      for (const [nr, nc] of neighbors) {
        if (board[nr][nc] === null) {
          liberties.add(`${nr},${nc}`);
        }
      }
    }
    return liberties.size;
  },
  captureStones(row, col, board, boardSize) {
    const placedColor = board[row][col];
    if (!placedColor) return [];
    const captured = [];
    const neighbors = this.getNeighbors(row, col, boardSize);
    for (const [nr, nc] of neighbors) {
      const opponentColor = board[nr][nc];
      if (opponentColor && opponentColor !== placedColor) {
        const group = this.getGroup(nr, nc, board, boardSize);
        const liberties = this.getLiberties(group, board, boardSize);
        if (liberties === 0) {
          for (const [gr, gc] of group) {
            board[gr][gc] = null;
            captured.push([gr, gc]);
          }
        }
      }
    }
    const selfGroup = this.getGroup(row, col, board, boardSize);
    const selfLiberties = this.getLiberties(selfGroup, board, boardSize);
    if (selfLiberties === 0) {
      for (const [gr, gc] of selfGroup) {
        board[gr][gc] = null;
        captured.push([gr, gc]);
      }
    }
    return captured;
  }
};

// 阻止按钮的触摸/鼠标事件冒泡到 DraggableWrapper，防止拖拽拦截点击
const stopDrag = (e) => e.stopPropagation();

const SimpleGoBoard = ({ sgfContent, gameInfo, downloadSGF }) => {
  const navigate = useNavigate();
  const [moves, setMoves] = useState([]);
  const [currentMove, setCurrentMove] = useState(0);
  const [boardSize, setBoardSize] = useState(19);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevMoveRef = useRef(0);
  const prevCapturedRef = useRef({ black: 0, white: 0 });
  const [boardState, setBoardState] = useState(Array(19).fill().map(() => Array(19).fill(null)));
  const screenWidth = useScreenWidth(); // 响应式屏幕宽度
  const [background, setBackground] = useState(PRESET_BACKGROUNDS.find(b => b.id === 'walnut') || PRESET_BACKGROUNDS[0]);
  const [capturedStones, setCapturedStones] = useState({ black: 0, white: 0 });
  const [handicapStones, setHandicapStones] = useState([]); // 让子棋子
  const [handicapCount, setHandicapCount] = useState(0); // 让子数

  // 电脑端缩放级别，持久化到localStorage
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const saved = localStorage.getItem('board-zoom-level');
      return saved ? parseInt(saved, 10) : 100;
    } catch (e) {
      return 100;
    }
  });

  const zoomIn = () => {
    setZoomLevel(prev => {
      const next = Math.min(200, prev + 10);
      localStorage.setItem('board-zoom-level', String(next));
      return next;
    });
  };

  const zoomOut = () => {
    setZoomLevel(prev => {
      const next = Math.max(50, prev - 10);
      localStorage.setItem('board-zoom-level', String(next));
      return next;
    });
  };

  const zoomReset = () => {
    setZoomLevel(100);
    localStorage.setItem('board-zoom-level', '100');
  };

  // 可拖动元素（仅电脑端）
  const moveButtons = useDraggable('move-buttons');
  const handCount = useDraggable('hand-count');
  const captureStats = useDraggable('capture-stats');
  const currentMoveInfo = useDraggable('current-move-info');
  const blackPlayerInfo = useDraggable('black-player-info');
  const whitePlayerInfo = useDraggable('white-player-info');
  const downloadBtn = useDraggable('download-btn');
  const backBtn = useDraggable('back-btn');
  const soundBtn = useDraggable('sound-btn');
  const backgroundBtns = useDraggable('background-btns');
  
  // 手机端/平板端顶部元素的拖动状态
  const mobileBackgroundBtns = useDraggable('mobile-background-btns');

  // 重置所有图标位置
  const resetAllPositions = () => {
    moveButtons.resetPosition();
    handCount.resetPosition();
    captureStats.resetPosition();
    currentMoveInfo.resetPosition();
    blackPlayerInfo.resetPosition();
    whitePlayerInfo.resetPosition();
    downloadBtn.resetPosition();
    backBtn.resetPosition();
    soundBtn.resetPosition();
    backgroundBtns.resetPosition();
    mobileBackgroundBtns.resetPosition();
  };

  useEffect(() => {
    if (!sgfContent) return;
    try {
      const parsed = parseSGFWithCaptures(sgfContent);
      setMoves(parsed.moves);
      setHandicapStones(parsed.handicapStones);
      setHandicapCount(parsed.handicapCount);
      setCurrentMove(0);
      const sizeMatch = sgfContent.match(/SZ\[(\d+)\]/);
      if (sizeMatch) {
        const size = parseInt(sizeMatch[1]);
        setBoardSize(size);
        // 初始棋盘放置让子/古谱初始子
        const initBoard = Array(size).fill().map(() => Array(size).fill(null));
        for (const hs of parsed.handicapStones) {
          initBoard[hs.row][hs.col] = hs.color || 'black';
        }
        setBoardState(initBoard);
      } else {
        // 即使没有SZ，也要放置让子/古谱初始子
        const initBoard = Array(19).fill().map(() => Array(19).fill(null));
        for (const hs of parsed.handicapStones) {
          initBoard[hs.row][hs.col] = hs.color || 'black';
        }
        setBoardState(initBoard);
      }
    } catch (error) {
      console.error('解析SGF失败:', error);
    }
  }, [sgfContent]);

  const parseSGFWithCaptures = (sgf) => {
    const moves = [];
    const handicapStones = []; // 让子棋子（AB属性）
    let handicapCount = 0; // HA属性：让子数
    
    // 解析HA[数字]让子数
    const haMatch = sgf.match(/HA\[(\d+)\]/);
    if (haMatch) {
      handicapCount = parseInt(haMatch[1]);
    }
    
    // 解析AB[xx][xx]...初始放置（Add Black）
    // AB属性在SGF中的格式：AB[pd][dp][dd]
    // 需在主分支解析之前单独提取，因为AB不是走子(B[xx])而是初始放置
    const abRegex = /AB((?:\[[a-s][a-s]\])+)/g;
    let abMatch;
    while ((abMatch = abRegex.exec(sgf)) !== null) {
      const positionsStr = abMatch[1];
      // 提取所有[xx]位置
      const posRegex = /\[([a-s])([a-s])\]/g;
      let posMatch;
      while ((posMatch = posRegex.exec(positionsStr)) !== null) {
        const col = posMatch[1].charCodeAt(0) - 97;
        const row = posMatch[2].charCodeAt(0) - 97;
        if (row >= 0 && row < 19 && col >= 0 && col < 19) {
          handicapStones.push({ row, col, color: 'black' });
        }
      }
    }

    // 解析AW[xx][xx]...初始放置（Add White）
    // 古谱分先棋：四角星位黑白各2子，AW属性格式同AB
    const awRegex = /AW((?:\[[a-s][a-s]\])+)/g;
    let awMatch;
    while ((awMatch = awRegex.exec(sgf)) !== null) {
      const awPositionsStr = awMatch[1];
      const awPosRegex = /\[([a-s])([a-s])\]/g;
      let awPosMatch;
      while ((awPosMatch = awPosRegex.exec(awPositionsStr)) !== null) {
        const col = awPosMatch[1].charCodeAt(0) - 97;
        const row = awPosMatch[2].charCodeAt(0) - 97;
        if (row >= 0 && row < 19 && col >= 0 && col < 19) {
          handicapStones.push({ row, col, color: 'white' });
        }
      }
    }
    
    // 只解析主分支，跳过所有变化图
    // 核心策略：每层只走第一个分支，遇到第二个分支则跳过
    // 这能正确处理：普通SGF、MultiGo格式、混合格式
    let stack = [{ enteredBranch: false }];
    
    for (let i = 0; i < sgf.length - 3; i++) {
      const ch = sgf[i];
      
      if (ch === '(') {
        const current = stack[stack.length - 1];
        if (!current.enteredBranch) {
          // 这是本层第一个分支，进入（主分支）
          current.enteredBranch = true;
          stack.push({ enteredBranch: false });
        } else {
          // 已经进入过分支，这是变化图，跳过整个变化图
          let skipDepth = 1;
          i++;
          while (i < sgf.length && skipDepth > 0) {
            if (sgf[i] === '(') skipDepth++;
            else if (sgf[i] === ')') skipDepth--;
            i++;
          }
          i--;
        }
      } else if (ch === ')') {
        if (stack.length > 1) stack.pop();
      } else if ((ch === 'B' || ch === 'W') && sgf[i + 1] === '[') {
        // 检查是否是有效的走棋位置（而非属性中的B/W）
        let isValidMove = false;
        if (i > 0) {
          const prev = sgf[i - 1];
          if (prev === ';' || prev === '(' || prev === ']' || prev === '\n' || prev === ' ' || prev === '\t') {
            isValidMove = true;
          }
        }
        
        if (isValidMove) {
          const color = ch;
          const c1 = sgf[i + 2];
          const c2 = sgf[i + 3];
          
          if (c1 >= 'a' && c1 <= 's' && c2 >= 'a' && c2 <= 's') {
            const col = c1.charCodeAt(0) - 97;
            const row = c2.charCodeAt(0) - 97;
            
            if (row >= 0 && row < 19 && col >= 0 && col < 19) {
              moves.push({
                color: color === 'B' ? 'black' : 'white',
                row,
                col,
                moveNumber: moves.length + 1
              });
            }
          }
        }
      }
    }
    
    // 计算提子
    const sizeMatch = sgf.match(/SZ\[(\d+)\]/);
    let bSize = 19;
    if (sizeMatch) bSize = parseInt(sizeMatch[1]);
    const board = Array(bSize).fill().map(() => Array(bSize).fill(null));
    
    // 先放置初始子（让子/古谱分先）
    for (const hs of handicapStones) {
      board[hs.row][hs.col] = hs.color || 'black';
    }
    
    for (let i = 0; i < moves.length; i++) {
      const move = moves[i];
      board[move.row][move.col] = move.color;
      const captured = GoUtils.captureStones(move.row, move.col, board, bSize);
      move.captured = captured.length;
    }
    
    if (handicapStones.length > 0) {
      console.log('🎯 让子棋：' + handicapStones.length + '子（HA=' + handicapCount + '），位置：', handicapStones.map(s => String.fromCharCode(97+s.col) + String.fromCharCode(97+s.row)));
    }
    console.log('📊 解析完成，主分支共 ' + moves.length + ' 手');
    if (moves.length > 400) {
      console.log('⚠️ 手数超过400，可能仍包含变化图');
    }
    
    return { moves, handicapStones, handicapCount };
  };

  useEffect(() => {
    if (moves.length === 0 && handicapStones.length === 0) return;
    
    const newBoard = Array(boardSize).fill().map(() => Array(boardSize).fill(null));
    // 先放置让子棋子（currentMove=0时也显示）
    for (const hs of handicapStones) {
      newBoard[hs.row][hs.col] = hs.color || 'black';
    }
    let blackCaptured = 0;
    let whiteCaptured = 0;
    for (let i = 0; i < Math.min(currentMove, moves.length); i++) {
      const move = moves[i];
      newBoard[move.row][move.col] = move.color;
      const captured = GoUtils.captureStones(move.row, move.col, newBoard, boardSize);
      if (move.color === 'black') {
        whiteCaptured += captured.length;
      } else {
        blackCaptured += captured.length;
      }
    }
    const currentCaptured = { black: blackCaptured, white: whiteCaptured };
    
    // 播放声音（仅在前进时）
    if (soundEnabled && currentMove > prevMoveRef.current) {
      // 播放落子声音
      playStoneSound();
      
      // 检查是否有提子
      const totalCaptured = blackCaptured + whiteCaptured;
      const prevTotalCaptured = prevCapturedRef.current.black + prevCapturedRef.current.white;
      if (totalCaptured > prevTotalCaptured) {
        // 有提子，播放提子声音
        playCaptureSound();
      }
    }
    
    prevMoveRef.current = currentMove;
    prevCapturedRef.current = currentCaptured;
    
    setBoardState(newBoard);
    setCapturedStones(currentCaptured);
  }, [currentMove, moves, boardSize, soundEnabled, handicapStones]);

  const checkMoveLimit = (targetMove) => {
    const user = localStorage.getItem('user');
    if (!user && targetMove > 100) {
      alert('未登录限观赏棋谱前100手，登录后无限制，新用户可在登录窗口注册');
      return false;
    }
    return true;
  };

  const goToFirst = () => setCurrentMove(0);
  const goToPrev10 = () => setCurrentMove(prev => Math.max(0, prev - 10));
  const goToPrev = () => setCurrentMove(prev => Math.max(0, prev - 1));
  const goToNext = () => {
    const target = currentMove + 1;
    if (checkMoveLimit(target)) {
      setCurrentMove(prev => Math.min(moves.length, prev + 1));
    }
  };
  const goToNext10 = () => {
    const target = currentMove + 10;
    if (checkMoveLimit(target)) {
      setCurrentMove(prev => Math.min(moves.length, prev + 10));
    }
  };
  const goToLast = () => {
    const target = moves.length;
    if (checkMoveLimit(target)) {
      setCurrentMove(moves.length);
    }
  };

  if (!sgfContent) {
    return (
      <div className="bg-amber-100 rounded-lg p-8 min-h-[400px] flex items-center justify-center">
        <p className="text-gray-700 text-center">正在加载棋谱...</p>
      </div>
    );
  }

  return (
    <div className="go-board-container relative h-screen flex flex-col overflow-hidden">
      {/* ========== 顶部固定栏 ========== */}
      {/* 手机端顶部栏 - 不使用DraggableWrapper，避免触摸事件拦截点击 */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-white shadow-md z-50 flex items-center justify-between px-2 sm:hidden">
        {/* 左侧：返回 + 声音 + 下载 */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-lg"
            title="返回搜索结果"
          >
            ←
          </button>
          
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-9 h-9 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-lg"
            title={soundEnabled ? "关闭声音" : "开启声音"}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          
          {downloadSGF && (
            <button
              onClick={downloadSGF}
              className="w-9 h-9 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-base font-bold"
              title="下载SGF"
            >
              📥
            </button>
          )}
        </div>
        
        {/* 右侧：手数 */}
        <div className="bg-amber-100 text-amber-900 px-3 py-1.5 rounded-full text-sm font-bold shadow-sm border border-amber-300">
          {currentMove}/{moves.length}
        </div>
      </div>
      
      {/* 平板端顶部栏 - 不使用DraggableWrapper，避免触摸事件拦截点击 */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white shadow-md z-50 flex items-center justify-between px-4 hidden sm:flex md:hidden">
        {/* 左侧：返回 + 声音 + 下载 */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-xl"
            title="返回搜索结果"
          >
            ←
          </button>
          
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-xl"
            title={soundEnabled ? "关闭声音" : "开启声音"}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          
          {downloadSGF && (
            <button
              onClick={downloadSGF}
              className="w-10 h-10 rounded-full border-2 border-gray-300 hover:border-amber-500 transition-all duration-200 bg-white shadow-sm flex items-center justify-center text-lg font-bold"
              title="下载SGF"
            >
              📥
            </button>
          )}
        </div>
        
        {/* 右侧：手数 */}
        <div className="bg-amber-100 text-amber-900 px-4 py-2 rounded-full text-base font-bold shadow-sm border border-amber-300">
          {currentMove}/{moves.length}
        </div>
      </div>
      
      {/* ========== 手机端/平板端中间棋盘区 ========== */}
      <div className="flex-1 flex flex-col items-center justify-center sm:pt-16 md:pt-16 pb-24 sm:pb-24 overflow-auto md:hidden">
        {/* 手机端棋手信息 - 放在棋盘上方 */}
        {(gameInfo?.black_player || gameInfo?.white_player) && (
          <div className="flex items-center justify-center gap-3 mb-1 sm:hidden">
            {gameInfo?.black_player && (
              <span className="text-xs font-semibold text-gray-800 bg-white bg-opacity-90 px-2 py-0.5 rounded shadow border border-gray-200">⚫ {gameInfo.black_player}</span>
            )}
            <span className="text-gray-400 text-xs">vs</span>
            {gameInfo?.white_player && (
              <span className="text-xs font-semibold text-gray-800 bg-white bg-opacity-90 px-2 py-0.5 rounded shadow border border-gray-200">⚪ {gameInfo.white_player}</span>
            )}
          </div>
        )}
        {/* 平板端棋手信息 */}
        {(gameInfo?.black_player || gameInfo?.white_player) && (
          <div className="hidden sm:flex md:hidden items-center justify-center gap-4 mb-2">
            {gameInfo?.black_player && (
              <span className="text-sm font-semibold text-gray-800 bg-white bg-opacity-90 px-3 py-1 rounded-lg shadow border border-gray-200">⚫ {gameInfo.black_player}</span>
            )}
            <span className="text-gray-400 text-sm">vs</span>
            {gameInfo?.white_player && (
              <span className="text-sm font-semibold text-gray-800 bg-white bg-opacity-90 px-3 py-1 rounded-lg shadow border border-gray-200">⚪ {gameInfo.white_player}</span>
            )}
          </div>
        )}
        {/* 对局信息悬浮区 */}
        <div className="relative">
          {/* 棋盘Canvas */}
          <BoardCanvas boardState={boardState} currentMove={currentMove} boardSize={boardSize} moves={moves} background={background} />
          
          {/* 手机端调色板 - 横排在棋盘下方 */}
          <div className="flex justify-center gap-2 mt-1 sm:hidden">
            {PRESET_BACKGROUNDS.map((bg) => (
              <button
                key={bg.id}
                onClick={() => setBackground(bg)}
                className={`w-5 h-5 rounded-full border-2 transition-all duration-200 hover:scale-110 ${
                  background.id === bg.id ? 'border-blue-500 shadow-md' : 'border-gray-300'
                }`}
                style={{ backgroundColor: bg.color }}
                title={bg.name}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* ========== 底部固定走子栏 ========== */}
      {/* 手机端底部走子栏 - 不使用DraggableWrapper，避免触摸事件拦截点击 */}
      <div className="fixed bottom-0 left-0 right-0 h-20 bg-white shadow-lg z-50 flex items-center justify-center px-2 sm:hidden">
          {/* 上排：首手，往后，往前，末手 */}
          <div className="flex flex-col gap-1.5">
          <div className="flex justify-center gap-1.5">
            <button
              onClick={goToFirst}
              disabled={currentMove === 0}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>⏮️</span>
            </button>
            <button
              onClick={goToPrev}
              disabled={currentMove === 0}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>◀️</span>
            </button>
            <button
              onClick={goToNext}
              disabled={currentMove >= moves.length}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>▶️</span>
            </button>
            <button
              onClick={goToLast}
              disabled={currentMove >= moves.length}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>⏭️</span>
            </button>
          </div>
          {/* 下排：后退-10，前进+10 */}
          <div className="flex justify-center gap-1.5">
            <button
              onClick={goToPrev10}
              disabled={currentMove < 10}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>◀️-</span>
            </button>
            <button
              onClick={goToNext10}
              disabled={currentMove + 10 >= moves.length}
              className="px-3 py-1.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
            >
              <span>▶️+</span>
            </button>
          </div>
          </div>
      </div>
      
      {/* 平板端底部走子栏 - 不使用DraggableWrapper，避免触摸事件拦截点击 */}
      <div className="fixed bottom-0 left-0 right-0 h-24 bg-white shadow-lg z-50 flex items-center justify-center px-4 hidden sm:flex md:hidden">
          <button
            onClick={goToFirst}
            disabled={currentMove === 0}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>⏮️</span>
          </button>
          <button
            onClick={goToPrev}
            disabled={currentMove === 0}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>◀️</span>
          </button>
          <button
            onClick={goToNext}
            disabled={currentMove >= moves.length}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>▶️</span>
          </button>
          <button
            onClick={goToLast}
            disabled={currentMove >= moves.length}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>⏭️</span>
          </button>
          <div className="w-0.5 h-8 bg-gray-300 mx-1"></div>
          <button
            onClick={goToPrev10}
            disabled={currentMove < 10}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>◀️-</span>
          </button>
          <button
            onClick={goToNext10}
            disabled={currentMove + 10 >= moves.length}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-base font-medium"
          >
            <span>▶️+</span>
          </button>
      </div>
      
      {/* 中等屏幕(768-1199px)底部走子栏 - 补充盲区 */}
      <div className="fixed bottom-0 left-0 right-0 h-16 bg-white shadow-lg z-50 flex items-center justify-center px-4 hidden md:flex min-[1200px]:hidden">
          <button
            onClick={goToFirst}
            disabled={currentMove === 0}
            className="px-3 py-2 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>⏮️</span>
          </button>
          <button
            onClick={goToPrev10}
            disabled={currentMove < 10}
            className="px-3 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>◀️-</span>
          </button>
          <button
            onClick={goToPrev}
            disabled={currentMove === 0}
            className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>◀️</span>
          </button>
          <button
            onClick={goToNext}
            disabled={currentMove >= moves.length}
            className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>▶️</span>
          </button>
          <button
            onClick={goToNext10}
            disabled={currentMove + 10 >= moves.length}
            className="px-3 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>▶️+</span>
          </button>
          <button
            onClick={goToLast}
            disabled={currentMove >= moves.length}
            className="px-3 py-2 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium"
          >
            <span>⏭️</span>
          </button>
      </div>
      
      {/* ========== 电脑端：保持原有布局 ========== */}
      <div className="relative flex-grow flex items-center justify-center min-h-0 overflow-hidden hidden md:flex">
        {/* 缩放容器：包裹棋盘Canvas + 右侧按钮竖条，整体缩放 */}
        <div style={{
          transform: `scale(${zoomLevel / 100})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease',
        }}>
          {/* 棋盘与按钮竖条的水平布局 */}
          <div className="flex items-center gap-2">
            {/* 棋盘Canvas */}
            <BoardCanvas boardState={boardState} currentMove={currentMove} boardSize={boardSize} moves={moves} background={background} />

            {/* 电脑端：右侧竖排走子按钮 - 紧贴棋盘右边缘 */}
            <div className="flex flex-col gap-2 z-20">
              <button onClick={goToFirst} disabled={currentMove === 0} className="px-3 py-2 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>⏮️</span>
              </button>
              <button onClick={goToPrev10} disabled={currentMove < 10} className="px-3 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>◀️-</span>
              </button>
              <button onClick={goToPrev} disabled={currentMove === 0} className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>◀️</span>
              </button>
              <button onClick={goToNext} disabled={currentMove >= moves.length} className="px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>▶️</span>
              </button>
              <button onClick={goToNext10} disabled={currentMove + 10 >= moves.length} className="px-3 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-lg hover:from-amber-700 hover:to-amber-800 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>▶️+</span>
              </button>
              <button onClick={goToLast} disabled={currentMove >= moves.length} className="px-3 py-2 bg-gradient-to-r from-amber-700 to-amber-800 text-white rounded-lg hover:from-amber-800 hover:to-amber-900 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm font-medium w-12 h-12">
                <span>⏭️</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* 中等屏幕(768-1199px)棋手姓名 - 左侧竖排 */}
        {(gameInfo?.black_player || gameInfo?.white_player) && (
          <div className="absolute top-1/2 left-2 -translate-y-1/2 flex flex-col items-center gap-2 min-[1200px]:hidden">
            {gameInfo?.black_player && (
              <div className="bg-white bg-opacity-90 rounded shadow border border-gray-200 px-1.5 py-1 flex flex-col items-center gap-0.5">
                <span className="text-sm">⚫</span>
                <span className="text-xs font-semibold text-gray-800"
                  style={{ writingMode: 'vertical-rl', letterSpacing: '2px' }}>{gameInfo.black_player}</span>
              </div>
            )}
            <span className="text-gray-400 text-[9px]">vs</span>
            {gameInfo?.white_player && (
              <div className="bg-white bg-opacity-90 rounded shadow border border-gray-200 px-1.5 py-1 flex flex-col items-center gap-0.5">
                <span className="text-sm">⚪</span>
                <span className="text-xs font-semibold text-gray-800"
                  style={{ writingMode: 'vertical-rl', letterSpacing: '2px' }}>{gameInfo.white_player}</span>
              </div>
            )}
          </div>
        )}
        
        {/* 中等屏幕(768-1199px)调色板 - 棋盘右侧竖排 */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 min-[1200px]:hidden">
          {PRESET_BACKGROUNDS.map((bg) => (
            <button
              key={bg.id}
              onClick={() => setBackground(bg)}
              className={`w-5 h-5 rounded-full border-2 transition-all duration-200 hover:scale-110 ${
                background.id === bg.id ? 'border-blue-500 shadow-md' : 'border-gray-300'
              }`}
              style={{ backgroundColor: bg.color }}
              title={bg.name}
            />
          ))}
        </div>
        
        {/* 手数显示 - 右上角 */}
        <DraggableWrapper 
          dragState={handCount}
          className="absolute top-2 right-2 bg-black bg-opacity-80 text-white px-2 py-0.5 rounded-lg text-xs font-bold shadow-lg md:px-3 md:py-1 md:text-sm"
          originalClassName="bg-black bg-opacity-80 text-white px-2 py-0.5 rounded-lg text-xs font-bold shadow-lg md:px-3 md:py-1 md:text-sm"
          enabled={false}
        >
          手数: {currentMove}/{moves.length}{handicapCount > 0 && <span className="ml-1 text-yellow-300">(让{handicapCount}子)</span>}
        </DraggableWrapper>

        {/* 提子统计 - 仅大屏显示，避免中等屏幕与棋盘重叠 */}
        <DraggableWrapper 
          dragState={captureStats}
          className="absolute top-2 left-2 bg-white bg-opacity-95 text-gray-800 px-2 py-1 rounded-lg text-xs shadow-lg border border-gray-200 hidden min-[1200px]:block"
          originalClassName="bg-white bg-opacity-95 text-gray-800 px-2 py-1 rounded-lg text-xs shadow-lg border border-gray-200"
          enabled={false}
        >
          <div className="font-semibold mb-1">📊 提子统计</div>
          <div>⚫ 黑方提掉: {capturedStones.black} 子</div>
          <div>⚪ 白方提掉: {capturedStones.white} 子</div>
        </DraggableWrapper>

        {/* 当前手数信息 - 左下角，仅大屏显示 */}
        {currentMove > 0 && moves[currentMove - 1] && (
          <DraggableWrapper 
            dragState={currentMoveInfo}
            className="absolute bottom-2 left-2 bg-white bg-opacity-95 text-gray-800 px-2 py-1 rounded-lg text-xs shadow-lg border border-gray-200 hidden min-[1200px]:block"
            originalClassName="bg-white bg-opacity-95 text-gray-800 px-2 py-1 rounded-lg text-xs shadow-lg border border-gray-200"
            enabled={false}
          >
            <div className="font-semibold">
              第{currentMove}手: {moves[currentMove - 1].color === 'black' ? '⚫ 黑' : '⚪ 白'}
              {moves[currentMove - 1].captured > 0 && (
                <span className="text-red-600 ml-0.5">提{moves[currentMove - 1].captured}子</span>
              )}
            </div>
            <div className="text-[9px] text-gray-600">
              位置: {String.fromCharCode(65 + (moves[currentMove - 1].col >= 8 ? moves[currentMove - 1].col + 1 : moves[currentMove - 1].col))}
              {boardSize - moves[currentMove - 1].row}
            </div>
          </DraggableWrapper>
        )}

        {/* 黑方信息 - 左侧，仅大屏显示 */}
        {gameInfo?.black_player && (
          <DraggableWrapper 
            dragState={blackPlayerInfo}
            className="absolute top-1/2 left-2 -translate-y-1/2 bg-white bg-opacity-95 text-gray-800 px-3 py-2 rounded-lg text-lg shadow-lg border border-gray-200 hidden min-[1200px]:block"
            originalClassName="bg-white bg-opacity-95 text-gray-800 px-3 py-2 rounded-lg text-lg shadow-lg border border-gray-200"
            enabled={false}
          >
            <div className="font-semibold">
              ⚫ 黑：{gameInfo.black_player}
            </div>
          </DraggableWrapper>
        )}

        {/* 白方信息 - 右侧，仅大屏显示 */}
        {gameInfo?.white_player && (
          <DraggableWrapper 
            dragState={whitePlayerInfo}
            className="absolute top-1/2 right-2 -translate-y-1/2 bg-white bg-opacity-95 text-gray-800 px-3 py-2 rounded-lg text-lg shadow-lg border border-gray-200 hidden min-[1200px]:block"
            originalClassName="bg-white bg-opacity-95 text-gray-800 px-3 py-2 rounded-lg text-lg shadow-lg border border-gray-200"
            enabled={false}
          >
            <div className="font-semibold">
              ⚪ 白：{gameInfo.white_player}
            </div>
          </DraggableWrapper>
        )}

        {/* 下载SGF按钮 - 右侧（平板+电脑端） */}
        {downloadSGF && (
          <DraggableWrapper 
            dragState={downloadBtn}
            className="absolute top-8 right-1.5 sm:right-3 hidden sm:block md:top-10"
            originalClassName=""
            enabled={false}
          >
            <button
              onClick={downloadSGF}
              className="bg-black bg-opacity-80 text-white px-2 py-0.5 rounded-lg text-xs font-bold shadow-lg hover w-auto text-center md:px-3 md:py-1 md:text-sm md:w-[100px]"
            >
              📥 下载
            </button>
          </DraggableWrapper>
        )}

        {/* 返回按钮 - 声音开关左边（平板+电脑端） */}
        <DraggableWrapper 
          dragState={backBtn}
          className="absolute top-14 right-10 hidden sm:block md:top-24 md:right-12"
          originalClassName=""
          enabled={false}
        >
          <button
            onClick={() => navigate(-1)}
            className="w-6 h-6 rounded-full border-2 transition-all duration-200 hover:scale-110 bg-white shadow-md flex items-center justify-center text-sm md:w-8 md:h-8 md:text-xl"
            title="返回搜索结果"
          >
            ←
          </button>
        </DraggableWrapper>
        
        {/* 声音开关按钮 - 下载按钮之下（平板+电脑端） */}
        <DraggableWrapper 
          dragState={soundBtn}
          className="absolute top-14 right-2 hidden sm:block md:top-24"
          originalClassName=""
          enabled={false}
        >
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-6 h-6 rounded-full border-2 transition-all duration-200 hover:scale-110 bg-white shadow-md flex items-center justify-center text-sm md:w-8 md:h-8 md:text-xl"
            title={soundEnabled ? "关闭声音" : "开启声音"}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </DraggableWrapper>

        {/* 背景选择按钮 - 右下角（平板+电脑端） */}
        <DraggableWrapper 
          dragState={backgroundBtns}
          className="absolute bottom-2 right-2 flex gap-0.5 hidden min-[1200px]:flex lg:gap-1"
          originalClassName="flex gap-0.5 lg:gap-1"
          enabled={false}
        >
          {PRESET_BACKGROUNDS.map((bg) => (
            <button
              key={bg.id}
              onClick={() => setBackground(bg)}
              className={`w-5 h-5 rounded-full border-2 transition-all duration-200 hover:scale-110 sm:w-5 sm:h-5 md:w-6 md:h-6 ${
                background.id === bg.id ? 'border-blue-500 shadow-md' : 'border-gray-300'
              }`}
              style={{ backgroundColor: bg.color }}
              title={bg.name}
            />
          ))}
        </DraggableWrapper>

        {/* 缩放控件 - 左下角上方（电脑端），与当前手数信息垂直错开 */}
        <div className="absolute bottom-14 left-2 hidden md:flex items-center gap-1 bg-white bg-opacity-90 rounded-lg shadow-md border border-gray-200 px-2 py-1 z-30">
          <button
            onClick={zoomOut}
            disabled={zoomLevel <= 50}
            className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            title="缩小"
          >−</button>
          <button
            onClick={zoomReset}
            className="min-w-[44px] h-7 rounded-md hover:bg-gray-100 transition-all duration-200 flex items-center justify-center text-xs font-bold text-gray-700"
            title="重置缩放"
          >{zoomLevel}%</button>
          <button
            onClick={zoomIn}
            disabled={zoomLevel >= 200}
            className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            title="放大"
          >+</button>
        </div>
      </div>
    </div>
  );
};

function BoardCanvas({ boardState, currentMove, boardSize, moves, background }) {
  const canvasRef = useRef(null);
  const [screenSize, setScreenSize] = useState('desktop');
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setScreenSize('mobile');
      } else if (width < 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  let cellSize, topPadding, bottomPadding, leftPadding, rightPadding;
         if (screenSize === 'mobile') {
           // 手机端自动计算最大格子尺寸，撑满宽度
           const maxAvailableWidth = window.innerWidth - 40;
           cellSize = Math.min(19, Math.floor(maxAvailableWidth / 19));
           topPadding = 12;
           bottomPadding = 6;
           leftPadding = 6;
           rightPadding = 18;
         } else if (screenSize === 'tablet') {
           // 平板端自动计算最大格子尺寸，撑满宽度
           const maxAvailableWidth = window.innerWidth - 80;
           cellSize = Math.min(26, Math.floor(maxAvailableWidth / 19));
           topPadding = 14;
           bottomPadding = 14;
           leftPadding = 16;
           rightPadding = 16;
         } else {
           // 电脑端：先确定边距，再根据可用空间计算最大cellSize
           topPadding = 22;
           bottomPadding = 22;
           leftPadding = 30;
           rightPadding = 30;

           // 电脑端：根据可用高度自适应，确保棋盘完全显示且尽可能大
           // 可用高度 = 窗口高度 - 导航栏/间隙预留
           const availableHeight = window.innerHeight - 120; 
           const availableWidth = window.innerWidth - 100;

           // 计算最大可能的cellSize (基于18个间隔)
           const cellH = Math.floor((availableHeight - topPadding - bottomPadding) / 18);
           const cellW = Math.floor((availableWidth - leftPadding - rightPadding) / 18);
           
           // 取宽高的较小值，不再设33的硬上限，让它随屏幕变大
           cellSize = Math.max(20, Math.min(cellH, cellW));
         }
  
  const gridSize = (boardSize - 1) * cellSize; // Total grid extent

  const boardWidth = gridSize + leftPadding + rightPadding;
  const boardHeight = gridSize + topPadding + bottomPadding;

  // Grid starts after leftPadding and topPadding
  const gridOffsetX = leftPadding;
  const gridOffsetY = topPadding;

  useEffect(() => {
          const ctx = canvasRef.current?.getContext('2d');
          if (!ctx) return;

          // 重置上下文变换，避免多次走子后缩放叠加导致棋盘放大
          ctx.resetTransform();
          
          // 高DPI适配，彻底解决棋盘和数字模糊问题
          const dpr = window.devicePixelRatio || 1;
          ctx.scale(dpr, dpr);

          ctx.clearRect(0, 0, boardWidth, boardHeight);

    // Background
    ctx.fillStyle = background?.color || '#D4B896';
    ctx.fillRect(0, 0, boardWidth, boardHeight);

    // Border
    ctx.strokeStyle = background?.border || '#6B4423';
    ctx.lineWidth = 3;
    ctx.strokeRect(gridOffsetX - 2, gridOffsetY - 2, gridSize + 4, gridSize + 4);

    // Grid lines
    ctx.lineWidth = 1;
    for (let i = 0; i < boardSize; i++) {
      ctx.beginPath();
      ctx.moveTo(gridOffsetX + i * cellSize, gridOffsetY);
      ctx.lineTo(gridOffsetX + i * cellSize, gridOffsetY + gridSize);
      ctx.stroke();
    }
    for (let i = 0; i < boardSize; i++) {
      ctx.beginPath();
      ctx.moveTo(gridOffsetX, gridOffsetY + i * cellSize);
      ctx.lineTo(gridOffsetX + gridSize, gridOffsetY + i * cellSize);
      ctx.stroke();
    }

    // Star points
    if (boardSize === 19) {
      const starPoints = [[3, 3], [3, 9], [3, 15], [9, 3], [9, 9], [9, 15], [15, 3], [15, 9], [15, 15]];
      ctx.fillStyle = background?.border || '#6B4423';
      starPoints.forEach(([row, col]) => {
        ctx.beginPath();
        ctx.arc(gridOffsetX + col * cellSize, gridOffsetY + row * cellSize, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Stones
    boardState.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (!cell) return;

        const x = gridOffsetX + colIndex * cellSize;
        const y = gridOffsetY + rowIndex * cellSize;
        const r = (cellSize - 4) / 2;

        const isLastMove = currentMove > 0 && moves[currentMove - 1]?.row === rowIndex && moves[currentMove - 1]?.col === colIndex;

        // Shadow
        ctx.beginPath();
        ctx.arc(x + 1, y + 1, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.fill();

        if (cell === 'black') {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = '#2a2a2a';
          ctx.fill();
          ctx.strokeStyle = '#1a1a1a';
          ctx.lineWidth = 1;
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = '#e8e8e8';
          ctx.fill();
          ctx.strokeStyle = '#cccccc';
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        if (isLastMove) {
          const moveNum = currentMove;
                const fontSize = r * 0.88;
                ctx.font = `900 ${fontSize}px system-ui, -apple-system, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          if (cell === 'black') {
            ctx.fillStyle = '#ffffff';
          } else {
            ctx.fillStyle = '#1a1a1a';
          }

          const text = moveNum > 999 ? '999+' : String(moveNum);
          ctx.fillText(text, x, y + 1);
        }
      });
    });

    // Column letters (top)
    // 重置textBaseline，避免被走子数字的'middle'影响坐标字母位置
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = background?.border || '#6B4423';
    const coordFontSize = screenSize === 'mobile' ? 9 : (screenSize === 'tablet' ? 10 : 11);
    ctx.font = `bold ${coordFontSize}px Arial`;
    ctx.textAlign = 'center';
    const topOffset = screenSize === 'mobile' ? 5 : 8;
    for (let i = 0; i < boardSize; i++) {
      const letter = String.fromCharCode(65 + i);
      ctx.fillText(letter, gridOffsetX + i * cellSize, gridOffsetY - topOffset);
    }

    // Row numbers (right)
    ctx.textAlign = 'left';
    const rightOffset = screenSize === 'mobile' ? 4 : 8;
    const verticalOffset = screenSize === 'mobile' ? 3 : 4;
    for (let i = 0; i < boardSize; i++) {
      ctx.fillText(String(i + 1), gridOffsetX + gridSize + rightOffset, gridOffsetY + i * cellSize + verticalOffset);
    }

  }, [boardState, currentMove, boardSize, moves, cellSize, gridSize, gridOffsetX, gridOffsetY, boardWidth, boardHeight, background]);

  return (
                <canvas ref={canvasRef} width={boardWidth * (window.devicePixelRatio || 1)} height={boardHeight * (window.devicePixelRatio || 1)} className="rounded-lg shadow-xl mx-auto" style={{ width: `${boardWidth}px`, height: `${boardHeight}px` }} />
  );
}

export default SimpleGoBoard;